/**
 * Client-side video export orchestrator (Phase 1).
 *
 * Renders the whole timeline into a single downloadable MP4 entirely in the
 * browser. See planning/video-download-export.md (Option A) for the design and
 * feasibility research. Pipeline:
 *
 *   1. Build manifest  - ordered playable clips (respecting the active filter)
 *                        + text/music overlay ranges via calculateTimelinePositions
 *   2. Fetch           - batch presigned URLs, download each _cropped_mini.mp4
 *                        and music file
 *   3. Mix audio       - OfflineAudioContext: lay each clip's baked audio
 *                        sequentially, overlay music with fade envelopes
 *                        (calculateFadeVolume), render to one PCM AudioBuffer
 *   4. Render video    - per clip: mediabunny decode -> draw frame to 960x720
 *                        canvas -> burn active text overlays -> encode
 *   5. Mux + download  - mediabunny Output (non-fragmented MP4) -> Blob ->
 *                        browser download as {projectName} {yyyy-MM-dd}.mp4
 *
 * The clips are already composited to 960x720 with baked clip volume (the same
 * _cropped_mini.mp4 the MSE player uses), so video frames are drawn 1:1 and clip
 * audio needs no per-clip volume scaling - only music is mixed on top.
 *
 * Capability is gated by canExportClientSide() (exportCapability.ts); callers
 * must check it before invoking exportTimeline().
 */

import { Video } from '../types/Video';
import { getPlayableVideos, getItemPlaybackDuration } from './videoPlaybackUtils';
import { sortVideosBySortOrder } from './sortOrderUtils';
import { calculateTimelinePositions, getActiveOverlays, TimelineRange } from './overlayCalculations';
import { calculateFadeVolume } from './audioFade';
import { drawTextOverlays } from './exportTextOverlay';
import { getOutroClip } from './brandOutro';
import { fetchMediaUrlBatch, getMediaApiUrl, EDGE_FUNCTION_CONTEXT } from './config';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

const EXPORT_W = 960;
const EXPORT_H = 720;
const EXPORT_FPS = 30;
const EXPORT_VIDEO_BITRATE = 2_500_000; // 2.5 Mbps - matches/slightly above clip bitrate
const EXPORT_AUDIO_BITRATE = 192_000;   // 192 kbps for the mixed master track
const AUDIO_SAMPLE_RATE = 48_000;
const AUDIO_CHANNELS = 2;
// Music that outlasts the assembled timeline fades out over this many seconds
// at the end, so the movie doesn't cut off mid-track.
const EXPORT_MUSIC_END_FADE_S = 3;

/**
 * Soft warning threshold. Above this many clips the export is still allowed but
 * the UI warns the user first (it can be slow and may fail on low-memory
 * devices). Below it, export starts with no prompt.
 */
export const EXPORT_WARN_CLIPS = 750;

/**
 * Hard safety cap. Beyond this many clips, client-side export is very likely to
 * exhaust tab memory / hit WebCodecs crashes (see planning doc gotchas) — at
 * ~2s/clip this is ~100 min of footage and >6000s of audio, past where
 * OfflineAudioContext is reliable. We block with a clear message rather than
 * crash the tab. Phase 2 (server-side) lifts this.
 */
export const MAX_EXPORT_CLIPS = 3000;

/**
 * The three real phases of an export, in order. These match exactly what the
 * code does (see exportTimeline): a `preparing` setup phase, one `rendering`
 * loop that downloads + decodes audio + renders frames per clip, and a
 * `finalizing` phase that mixes music and writes the file. `done` is terminal.
 *
 * IMPORTANT: there is no separate "downloading" or "mixing audio" phase — after
 * the single-fetch refactor those happen inside `rendering` (download/clip
 * audio) and `finalizing` (music mix). The UI stepper mirrors these phases 1:1,
 * so don't reintroduce phases that don't reflect the actual control flow.
 */
export type ExportPhase =
  | 'preparing'
  | 'rendering'
  | 'finalizing'
  | 'done';

export interface ExportProgress {
  phase: ExportPhase;
  /** 0-100 overall. */
  percent: number;
  /** Human-readable detail, e.g. "Clip 42 of 200". */
  detail: string;
}

export interface ExportOptions {
  /** Clips currently visible/playable (already date-filtered by the caller). */
  filteredVideos: Video[];
  projectName: string;
  onProgress?: (p: ExportProgress) => void;
  signal?: AbortSignal;
}

export class ExportCanceledError extends Error {
  constructor() {
    super('Export canceled');
    this.name = 'ExportCanceledError';
  }
}

export class ExportTooLargeError extends Error {
  constructor(public clipCount: number) {
    super(`Project has ${clipCount} clips, exceeding the client-side export limit of ${MAX_EXPORT_CLIPS}`);
    this.name = 'ExportTooLargeError';
  }
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new ExportCanceledError();
}

/**
 * Each phase's share of the 0-100 bar. `rendering` is the bulk of the work
 * (per-clip download + audio decode + frame encode); `finalizing` covers music
 * mixing + mux + file write.
 */
const PHASE_WEIGHTS = {
  preparing: 5,
  rendering: 85,
  finalizing: 10,
} as const;

/**
 * Export the timeline to a single MP4 and trigger a browser download.
 * Throws ExportCanceledError if the signal aborts, ExportTooLargeError if the
 * clip count exceeds MAX_EXPORT_CLIPS.
 *
 * Wraps the render so that ANY error raised while the signal is aborted is
 * normalized to ExportCanceledError. Aborting an in-flight fetch / decode
 * throws a DOMException('AbortError') (not our error type); without this
 * normalization a user-initiated cancel would surface as a generic
 * "something went wrong" toast. ExportTooLargeError is re-thrown untouched.
 */
export async function exportTimeline(opts: ExportOptions): Promise<void> {
  try {
    await runExport(opts);
  } catch (err) {
    if (err instanceof ExportTooLargeError) throw err;
    if (opts.signal?.aborted || err instanceof ExportCanceledError) {
      throw new ExportCanceledError();
    }
    throw err;
  }
}

async function runExport(opts: ExportOptions): Promise<void> {
  const { filteredVideos, projectName, onProgress, signal } = opts;

  const report = (phase: ExportPhase, percent: number, detail: string) =>
    onProgress?.({ phase, percent: Math.min(100, Math.max(0, Math.round(percent))), detail });

  report('preparing', 0, 'Preparing export…');

  // --- 1. Build manifest ---------------------------------------------------
  // Playback order is always oldest->newest by sort_order, respecting the
  // active filter (filteredVideos is what the caller currently shows).
  const sortedAll = sortVideosBySortOrder(filteredVideos);
  const playableClips = sortVideosBySortOrder(getPlayableVideos(filteredVideos));

  if (playableClips.length === 0) {
    throw new Error('No playable clips to export');
  }
  if (playableClips.length > MAX_EXPORT_CLIPS) {
    throw new ExportTooLargeError(playableClips.length);
  }

  // Timeline ranges (start/end seconds) for every item, including overlays.
  const ranges = calculateTimelinePositions(sortedAll);
  const clipRanges = new Map<string, TimelineRange>();
  for (const r of ranges) {
    if (r.item.media_type === 'video' || r.item.media_type === 'photo' || !r.item.media_type) {
      clipRanges.set(r.item.id, r);
    }
  }

  const totalDuration = playableClips.reduce((sum, c) => sum + getItemPlaybackDuration(c), 0);

  throwIfAborted(signal);

  const { Input, Output, BlobSource, BufferTarget, Mp4OutputFormat, VideoSampleSink, CanvasSource, AudioBufferSource, MP4, QTFF } =
    await import('mediabunny');

  // --- 2. Resolve presigned URLs (chunked) --------------------------------
  // The batch endpoint signs every URL in parallel inside one invocation, so a
  // single 500+ item request overwhelms it (HTTP 500). Chunk like the MSE
  // player does. URLs (not blobs) are small, so we can hold them all.
  report('preparing', PHASE_WEIGHTS.preparing / 2, `Preparing ${playableClips.length} clips`);
  const clipUrls = await resolveClipUrls(playableClips, signal);

  // --- 3. Set up the audio mix context + muxer ----------------------------
  // SINGLE-FETCH design: each clip's blob is downloaded exactly ONCE, in the
  // render loop below, and used for BOTH its audio (decoded into this offline
  // context) and its video frames. The encoded blob is released each iteration
  // so memory stays bounded; only the decoded clip AudioBuffers persist in the
  // offline context (unavoidable for mixing). Music is fetched once separately.
  const musicItems = sortedAll.filter(v => v.media_type === 'music');
  const mixLengthSamples = Math.max(1, Math.ceil(totalDuration * AUDIO_SAMPLE_RATE));
  const offlineAudio = new OfflineAudioContext(AUDIO_CHANNELS, mixLengthSamples, AUDIO_SAMPLE_RATE);

  const target = new BufferTarget();
  const output = new Output({
    // Non-fragmented MP4 for a clean downloadable file (in-memory fast-start
    // moves the moov box to the front so the file is seekable immediately).
    format: new Mp4OutputFormat({ fastStart: 'in-memory' }),
    target,
  });

  const canvas = document.createElement('canvas');
  canvas.width = EXPORT_W;
  canvas.height = EXPORT_H;
  const ctx = canvas.getContext('2d', { alpha: false })!;

  const videoSource = new CanvasSource(canvas, {
    codec: 'avc',
    bitrate: EXPORT_VIDEO_BITRATE,
  });
  output.addVideoTrack(videoSource, { frameRate: EXPORT_FPS });

  const audioSource = new AudioBufferSource({ codec: 'aac', bitrate: EXPORT_AUDIO_BITRATE });
  output.addAudioTrack(audioSource);

  await output.start();

  // --- 4. Render video frames + decode clip audio (one fetch per clip) -----
  const renderStartPercent = PHASE_WEIGHTS.preparing;
  const renderWeight = PHASE_WEIGHTS.rendering;

  let emittedTime = 0; // absolute output timeline position (seconds)
  for (let i = 0; i < playableClips.length; i++) {
    throwIfAborted(signal);
    const clip = playableClips[i];
    const range = clipRanges.get(clip.id)!; // croppedDuration-based timeline, for overlay timing
    const clipDuration = getItemPlaybackDuration(clip);

    // Anchor this clip's audio to where its VIDEO actually starts. Video frames
    // advance by each clip's real decoded frame durations (emittedTime), which
    // differ slightly from croppedDuration — so scheduling audio on the
    // croppedDuration timeline (clipRanges.start) drifts ~46ms/clip, leaving the
    // audio track seconds longer than video over a long movie (observed ~17s on
    // a 12-min export). Using emittedTime keeps audio and video locked together.
    const clipAudioStart = emittedTime;

    // Fetch this clip's blob ONCE. Used for both audio decode and video frames,
    // then released (GC'd) so memory stays bounded for 500+ clip projects.
    const url = clipUrls.get(clip.id);
    if (!url) throw new Error(`No URL for clip "${clip.name}" (${clip.id.slice(0, 8)})`);
    const resp = await fetch(url, { signal });
    if (!resp.ok) throw new Error(`Failed to download clip "${clip.name}": HTTP ${resp.status}`);
    const bytes = await resp.arrayBuffer();

    // (a) Decode this clip's baked audio into the mix at the clip's video start.
    // decodeAudioData detaches the buffer it's given, so hand it a copy and
    // keep `bytes` intact for the video demux below.
    try {
      const audioBuf = await offlineAudio.decodeAudioData(bytes.slice(0));
      const node = offlineAudio.createBufferSource();
      node.buffer = audioBuf;
      node.connect(offlineAudio.destination);
      node.start(clipAudioStart);
    } catch (err) {
      if (signal?.aborted) throw new ExportCanceledError();
      // Undecodable clip audio shouldn't fail the export — it contributes
      // silence; the video still renders.
      console.error(`[Export] Clip audio decode failed for "${clip.name}":`, err);
    }

    // (b) Decode + composite this clip's video frames.
    const input = new Input({
      formats: [MP4, QTFF],
      source: new BlobSource(new Blob([bytes], { type: 'video/mp4' })),
    });
    const videoTrack = await input.getPrimaryVideoTrack();
    if (!videoTrack) {
      input.dispose();
      throw new Error(`Clip "${clip.name}" has no video track`);
    }
    const sink = new VideoSampleSink(videoTrack);

    // Walk this clip's decoded frames. We re-time them onto the output's
    // absolute timeline and burn whatever text overlays are active at that
    // global position.
    let lastFrameDrawn = false;
    for await (const sample of sink.samples()) {
      throwIfAborted(signal);

      const globalTime = range.start + sample.timestamp;

      // Composite: clips are already 960x720 (the player's _cropped_mini.mp4),
      // so draw the frame 1:1 onto the export canvas.
      sample.draw(ctx, 0, 0, EXPORT_W, EXPORT_H);

      // Burn active text overlays at this global timeline position.
      const { textItems } = getActiveOverlays(sortedAll, globalTime);
      if (textItems.length > 0) {
        drawTextOverlays(ctx, textItems.map(t => ({ item: t.item })));
      }

      // Clamp per-frame duration.
      //
      // Minis keep the source camera's variable frame rate, so a single
      // sample can claim 200-336ms (measured on the reference project).
      // Trusting that verbatim reproduces the playback freeze in the exported
      // file — the same frame, held for a third of a second. Two nominal
      // frames is generous enough for legitimately slower source cadence
      // (15fps clips exist in this project) while capping the pathological
      // values.
      //
      // ⚠️ TRADEOFF: this makes an affected clip slightly SHORTER in the export
      // than it is in the player (a 336ms sample contributes 66.7ms instead).
      // Audio stays in sync — it is anchored to `emittedTime`, so it shortens by
      // the same amount — but total movie length shrinks by the clamped time.
      // That is deliberate: a marginally shorter clip is preferable to a visibly
      // frozen frame. Only clips with pathological samples (~11 of 633 on the
      // reference project) ever hit the clamp.
      const nominalFrameDuration = 1 / EXPORT_FPS;
      const frameDuration = sample.duration > 0
        ? Math.min(sample.duration, 2 * nominalFrameDuration)
        : nominalFrameDuration;
      await videoSource.add(emittedTime, frameDuration);
      emittedTime += frameDuration;
      lastFrameDrawn = true;

      sample.close();
    }

    input.dispose();

    if (!lastFrameDrawn) {
      // Defensive: a clip that yielded no frames would desync audio/video.
      // Emit a single black frame for its duration so the timeline stays aligned.
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, EXPORT_W, EXPORT_H);
      await videoSource.add(emittedTime, clipDuration);
      emittedTime += clipDuration;
    }

    report(
      'rendering',
      renderStartPercent + (renderWeight * (i + 1)) / playableClips.length,
      `Clip ${i + 1} of ${playableClips.length}`,
    );
  }

  // --- 4b. Branded ending clip (#67) --------------------------------------
  // The downloaded file is the artifact most likely to be shared outside the
  // app, so it carries the same ending card the player shows. Decoded through
  // the same path as every other clip; it is already 960x720, so it draws 1:1.
  //
  // Deliberately NOT counted toward MAX_EXPORT_CLIPS or the progress
  // denominator — those describe the user's own footage.

  // The user's movie ends HERE. Captured before the outro extends emittedTime,
  // because the music timeline below is scaled against it: including the outro
  // would stretch every track and drag the end-fade onto the brand card.
  const movieEndTime = emittedTime;

  try {
    const outroBlob = await getOutroClip();
    if (outroBlob) {
      const outroInput = new Input({
        formats: [MP4, QTFF],
        source: new BlobSource(outroBlob),
      });
      try {
        const outroTrack = await outroInput.getPrimaryVideoTrack();
        if (outroTrack) {
          for await (const sample of new VideoSampleSink(outroTrack).samples()) {
            throwIfAborted(signal);
            sample.draw(ctx, 0, 0, EXPORT_W, EXPORT_H);
            const frameDuration = sample.duration > 0 ? sample.duration : 1 / EXPORT_FPS;
            await videoSource.add(emittedTime, frameDuration);
            emittedTime += frameDuration;
            sample.close();
          }
        }
      } finally {
        outroInput.dispose();
      }
    }
  } catch (err) {
    if (signal?.aborted) throw new ExportCanceledError();
    // A missing outro must never fail an otherwise-complete export.
    console.error('[Export] Outro render failed:', err);
  }

  // --- 5. Mix in music, render audio, attach to muxer ---------------------
  // Clip audio is already scheduled into offlineAudio (in the loop). Add music
  // with fade envelopes, render the whole mix to one buffer, then add it to the
  // (already-populated) audio track. Music is fetched once here. This is part of
  // the user-facing "Finishing up" phase (music mix happens after the loop).
  report('finalizing', renderStartPercent + renderWeight, 'Finishing up');
  // emittedTime is now the REAL video length (sum of actual frame durations),
  // which is slightly shorter than totalDuration (the croppedDuration sum the
  // overlay timeline uses). Map music from the croppedDuration timeline onto the
  // real clock so it stays aligned with the drift-corrected clip audio and the
  // end-fade lands at the true movie end.
  // Music is scored against the USER'S movie, not the outro that follows it —
  // hence movieEndTime rather than emittedTime.
  const realDuration = movieEndTime;
  const driftScale = totalDuration > 0 ? realDuration / totalDuration : 1;
  for (const music of musicItems) {
    throwIfAborted(signal);
    const range = ranges.find(r => r.item.id === music.id);
    if (!range) continue;
    try {
      const blob = await fetchMusicBlob(music, signal);
      if (!blob) continue;
      const buf = await offlineAudio.decodeAudioData(await blob.arrayBuffer());
      const node = offlineAudio.createBufferSource();
      node.buffer = buf;
      const gain = offlineAudio.createGain();
      node.connect(gain);
      gain.connect(offlineAudio.destination);
      // Scale the music's start onto the real timeline; the envelope clamps its
      // end (and the 3s end-fade) to realDuration.
      const scaledRange: TimelineRange = { ...range, start: range.start * driftScale };
      applyMusicEnvelope(gain, music, scaledRange, realDuration);
      node.start(scaledRange.start);
    } catch (err) {
      if (signal?.aborted) throw new ExportCanceledError();
      console.error(`[Export] Music decode failed for "${music.name}":`, err);
    }
  }
  let mixedAudio = await offlineAudio.startRendering();
  // The mix buffer was sized to totalDuration (croppedDuration sum), but the
  // real video is `realDuration` (slightly shorter). Trim the trailing silence
  // so the audio track length matches the video instead of running a few
  // seconds long. Only trims when meaningfully longer (>50ms).
  if (realDuration > 0 && mixedAudio.duration - realDuration > 0.05) {
    const keep = Math.min(mixedAudio.length, Math.ceil(realDuration * AUDIO_SAMPLE_RATE));
    const trimmed = new AudioBuffer({
      length: keep,
      numberOfChannels: mixedAudio.numberOfChannels,
      sampleRate: AUDIO_SAMPLE_RATE,
    });
    for (let ch = 0; ch < mixedAudio.numberOfChannels; ch++) {
      trimmed.copyToChannel(mixedAudio.getChannelData(ch).subarray(0, keep), ch);
    }
    mixedAudio = trimmed;
  }
  await audioSource.add(mixedAudio);

  // --- 6. Finalize + download ---------------------------------------------
  report('finalizing', 100 - PHASE_WEIGHTS.finalizing, 'Finishing up');
  await output.finalize();

  if (!target.buffer) throw new Error('Export produced no output');
  const mp4Blob = new Blob([target.buffer], { type: 'video/mp4' });

  const downloadDate = format(new Date(), 'yyyy-MM-dd');
  triggerDownload(mp4Blob, `${sanitizeFileName(projectName)} ${downloadDate}.mp4`);

  report('done', 100, 'Download ready');
}

/**
 * Resolve presigned URLs for all clips in chunks. The serve-media-batch Edge
 * Function signs every URL in parallel within a single invocation, so one big
 * request (500+ ids) returns HTTP 500. Chunk it (the MSE player uses 10; 25 is
 * comfortable for a one-shot resolve). Chunks run sequentially to avoid a
 * thundering-herd of Edge invocations.
 */
const URL_RESOLVE_CHUNK = 25;

async function resolveClipUrls(
  clips: Video[],
  signal?: AbortSignal,
): Promise<Map<string, string>> {
  const urls = new Map<string, string>();
  for (let i = 0; i < clips.length; i += URL_RESOLVE_CHUNK) {
    throwIfAborted(signal);
    const chunk = clips.slice(i, i + URL_RESOLVE_CHUNK);
    const map = await fetchMediaUrlBatch(
      chunk.map(c => ({ id: c.id, context: EDGE_FUNCTION_CONTEXT.PLAYER })),
      signal,
    );
    for (const clip of chunk) {
      const entry = map.get(clip.id);
      if (!entry?.url) throw new Error(`No URL for clip "${clip.name}" (${clip.id.slice(0, 8)})`);
      urls.set(clip.id, entry.url);
    }
  }
  return urls;
}

/**
 * Schedule gain automation on a music track's GainNode matching the on-screen
 * fade behavior (calculateFadeVolume). We sample the fade curve at a fixed step
 * and use setValueAtTime so the offline render reproduces the same envelope the
 * live useMusicPlayer applies.
 */
function applyMusicEnvelope(
  gain: GainNode,
  music: Video,
  range: TimelineRange,
  totalDuration: number,
): void {
  const baseVolume = music.volume ?? 1.0;
  const fadeIn = music.music_fade_in || 0;
  const fadeOut = music.music_fade_out || 0;
  // Music plays from its start until the track duration ends OR the timeline ends.
  const trackDuration = range.end - range.start;
  const audibleEnd = Math.min(range.end, totalDuration);
  const audibleDuration = audibleEnd - range.start;

  // If the music outlasts the assembled timeline, it would otherwise cut off
  // abruptly at the end. Fade it out over the last EXPORT_MUSIC_END_FADE_S
  // seconds so the movie ends gracefully. Only applies when truncated.
  const truncated = range.end > totalDuration + 0.01;
  const endFadeStart = truncated ? Math.max(0, audibleDuration - EXPORT_MUSIC_END_FADE_S) : Infinity;

  /** Combined gain: the track's own fade envelope AND the timeline-end fade. */
  const volAt = (t: number): number => {
    let vol = calculateFadeVolume(t, trackDuration, fadeIn, fadeOut, baseVolume);
    if (t >= endFadeStart) {
      const remaining = audibleDuration - t;
      const endGain = Math.max(0, Math.min(1, remaining / EXPORT_MUSIC_END_FADE_S));
      vol = Math.min(vol, baseVolume * endGain); // never exceed either envelope
    }
    return vol;
  };

  const STEP = 0.05; // 50ms automation steps - smooth and cheap
  for (let t = 0; t <= audibleDuration; t += STEP) {
    gain.gain.setValueAtTime(volAt(t), range.start + t);
  }
  // Ensure a final point at the audible end (0 when truncated → clean fade).
  gain.gain.setValueAtTime(truncated ? 0 : volAt(audibleDuration), audibleEnd);
}

/**
 * Fetch a music track's audio as a Blob via the Edge Function (mirrors
 * useMusicPlayer.getMusicUrl - template music uses path, uploaded uses id).
 */
async function fetchMusicBlob(music: Video, signal?: AbortSignal): Promise<Blob | null> {
  if (!music.storage_path) {
    console.error('[Export] Music item missing storage_path:', music.id);
    return null;
  }
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error('[Export] No session for music fetch');
      return null;
    }
    const edgeUrl = music.storage_path.startsWith('template-music/')
      ? getMediaApiUrl(music.storage_path)
      : getMediaApiUrl(music.id);

    const resp = await fetch(edgeUrl, {
      headers: { Authorization: `Bearer ${session.access_token}` },
      signal,
    });
    if (!resp.ok) {
      console.error('[Export] Music edge fetch failed:', resp.status);
      return null;
    }
    const data = await resp.json();
    if (!data.url) return null;

    const audioResp = await fetch(data.url, { signal });
    if (!audioResp.ok) {
      console.error('[Export] Music R2 fetch failed:', audioResp.status);
      return null;
    }
    return await audioResp.blob();
  } catch (err) {
    if (signal?.aborted) throw new ExportCanceledError();
    console.error('[Export] Music fetch error:', err);
    return null;
  }
}

function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a tick so the download has been handed off to the browser.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function sanitizeFileName(name: string): string {
  const cleaned = name.replace(/[/\\?%*:|"<>]/g, '-').trim();
  return cleaned || 'movie';
}
